import { useState } from "react";
import { Pressable, ActivityIndicator, Text } from "react-native";
import styled from "styled-components/native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { httpsCallable, getFunctions } from "firebase/functions";
import { doc, setDoc, Timestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useRouter, useLocalSearchParams } from "expo-router";
import { useClub } from "@/contexts/ClubContext";
import { useTeamConfig } from "@/hooks/useTeamConfig";
import { useLeagues } from "@/hooks/useLeagues";
import { createMatch, updateMatch } from "@/lib/services";
import { useAuth } from "@/contexts/AuthContext";
import { colors, radius } from "@/lib/theme";
import type { MatchVenue, MatchType } from "@/lib/types";
import ContentWidth from "@/components/ContentWidth";
import { DateInput, TimeInput } from "@/components/DateTimeInputs";

const functions = getFunctions(undefined, "europe-west2");

// ── Styled Components ──────────────────────────────────

const Screen = styled.View`
  flex: 1;
  background-color: ${colors.bg};
`;

const Label = styled.Text`
  font-size: 11px;
  font-weight: 700;
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  margin-top: 16px;
`;

const Input = styled.TextInput`
  background-color: ${colors.bg};
  border: 1px solid ${colors.line};
  border-radius: ${radius.md}px;
  padding: 12px 14px;
  font-size: 15px;
  color: ${colors.ink};
`;

const PillRow = styled.View`
  flex-direction: row;
  gap: 8px;
  flex-wrap: wrap;
`;

const Pill = styled.Pressable<{ active?: boolean }>`
  background-color: ${(p) => (p.active ? colors.brand : colors.bg)};
  border-radius: 999px;
  padding: 8px 14px;
  border: 1px solid ${(p) => (p.active ? colors.brand : colors.line)};
`;

const PillText = styled.Text<{ active?: boolean }>`
  font-size: 13px;
  font-weight: 700;
  color: ${(p) => (p.active ? "#fff" : colors.inkSecondary)};
`;

const PrimaryBtn = styled.Pressable`
  background-color: ${colors.brand};
  border-radius: ${radius.card}px;
  padding: 16px;
  align-items: center;
  margin-top: 24px;
`;

const PrimaryBtnText = styled.Text`
  color: #fff;
  font-size: 16px;
  font-weight: 700;
`;

const CancelBtn = styled.Pressable`
  padding: 12px;
  align-items: center;
  margin-top: 8px;
`;

const CancelBtnText = styled.Text`
  color: ${colors.inkMuted};
  font-size: 15px;
  font-weight: 700;
`;

const LinkText = styled.Text`
  font-size: 13px;
  color: ${colors.brand};
  font-weight: 700;
  margin-top: 8px;
`;

const Dropdown = styled.View`
  background-color: ${colors.white};
  border: 1px solid ${colors.line};
  border-radius: ${radius.md}px;
  margin-top: 4px;
  max-height: 200px;
  overflow: hidden;
`;

const DropdownItem = styled.Pressable`
  padding: 12px 14px;
  border-bottom-width: 1px;
  border-bottom-color: ${colors.line};
`;

const DropdownText = styled.Text`
  font-size: 14px;
  color: ${colors.ink};
  font-weight: 500;
`;

const DropdownTextCustom = styled.Text`
  font-size: 14px;
  color: ${colors.brand};
  font-weight: 700;
`;

const SelectedOpponent = styled.Pressable`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: ${colors.brand50};
  border: 1px solid ${colors.brandLight};
  border-radius: ${radius.md}px;
  padding: 10px 14px;
  margin-top: 8px;
`;

const SelectedOpponentText = styled.Text`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.brandDark};
`;

const ImportBox = styled.View`
  background-color: ${colors.brand50};
  border-radius: ${radius.md}px;
  border: 1px solid ${colors.brandLight};
  padding: 14px;
`;

const ImportDesc = styled.Text`
  font-size: 13px;
  color: ${colors.brandDark};
  line-height: 20px;
`;

const ImportError = styled.Text`
  font-size: 13px;
  color: ${colors.danger};
  font-weight: 600;
  margin-top: 8px;
`;

const ImportActions = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 16px;
  margin-top: 12px;
`;

const ImportBtn = styled.Pressable`
  background-color: ${colors.brand};
  border-radius: ${radius.md}px;
  padding: 10px 16px;
`;

const ImportBtnText = styled.Text`
  color: #fff;
  font-size: 13px;
  font-weight: 700;
`;

const ImportFoundText = styled.Text`
  font-size: 13px;
  font-weight: 700;
  color: ${colors.brandDark};
  margin-top: 12px;
  margin-bottom: 8px;
`;

const ChipWrap = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.View`
  background-color: ${colors.white};
  border-radius: 999px;
  padding: 4px 10px;
  border: 1px solid ${colors.brandLight};
`;

const ChipText = styled.Text`
  font-size: 12px;
  font-weight: 700;
  color: ${colors.brandDark};
`;

const Spacer = styled.View<{ h?: number }>`
  height: ${(p) => p.h || 32}px;
`;

// ── Component ──────────────────────────────────────────

export default function CreateFixtureScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{
    matchId?: string; team?: string; opponent?: string; date?: string;
    time?: string; venue?: string; type?: string; location?: string;
  }>();
  const isEdit = !!params.matchId;
  const { user } = useAuth();
  const { club, membership } = useClub();
  const { isAnyCaptain } = useTeamConfig(club?.id);
  const { getOpponentsForTeam, allOpponents } = useLeagues(club?.id);

  const [form, setForm] = useState({
    team: params.team || "1st XI",
    opponent: params.opponent || "",
    date: params.date || "",
    time: params.time || "13:00",
    venue: (params.venue || "home") as MatchVenue,
    type: (params.type || "league") as MatchType,
    location: params.location || "",
  });
  const [saving, setSaving] = useState(false);
  const [opponentSearch, setOpponentSearch] = useState("");
  const [manualEntry, setManualEntry] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const [importResult, setImportResult] = useState<{ teams: string[]; divisionName: string } | null>(null);
  const [savingImport, setSavingImport] = useState(false);

  const canCreate = membership?.role === "admin" || isAnyCaptain(user?.uid);
  const teamOpponents = getOpponentsForTeam(form.team);
  const hasLeagueForTeam = teamOpponents.length > 0;
  const opponents = hasLeagueForTeam ? teamOpponents : allOpponents;
  const showDropdownList = opponents.length > 0 && !manualEntry;
  const filteredOpponents = opponentSearch
    ? opponents.filter((o) => o.toLowerCase().includes(opponentSearch.toLowerCase()))
    : opponents;

  const [error, setError] = useState("");

  const handleCreate = async () => {
    if (!club || !user) return;
    if (!form.opponent) { setError("Opponent is required."); return; }
    if (!form.date) { setError("Date is required."); return; }
    if (!form.location?.trim()) { setError("Location is required (postcode or address)."); return; }
    setError("");
    setSaving(true);
    try {
      if (isEdit && params.matchId) {
        await updateMatch(club.id, params.matchId, {
          opponent: form.opponent,
          date: form.date,
          time: form.time,
          venue: form.venue,
          type: form.type,
          location: form.location || null,
        });
      } else {
        await createMatch(club.id, { ...form, clubName: club.name, createdBy: user.uid, availabilityLeadDays: club.availabilityLeadDays ?? 7 });
      }
      router.back();
    } catch (err: any) {
      console.error("Save fixture error:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleImport = async () => {
    if (!importUrl.trim()) return;
    setImporting(true); setImportError(""); setImportResult(null);
    try {
      const result = await httpsCallable(functions, "scrapeLeagueTeams")({ url: importUrl.trim() });
      setImportResult(result.data as any);
    } catch (err: any) { setImportError(err.message || "Failed to fetch teams."); }
    finally { setImporting(false); }
  };

  const handleSaveImport = async () => {
    if (!importResult || !club) return;
    setSavingImport(true);
    try {
      await setDoc(doc(db, "clubs", club.id, "leagues", form.team.replace(/\s+/g, "-").toLowerCase()), {
        team: form.team, divisionUrl: importUrl.trim(), divisionName: importResult.divisionName,
        opponents: importResult.teams, updatedAt: Timestamp.now(),
      });
      setShowImport(false); setImportUrl(""); setImportResult(null);
    } catch (err: any) { setImportError(err.message || "Failed to save."); }
    finally { setSavingImport(false); }
  };

  return (
    <Screen>
      <KeyboardAwareScroll showsVerticalScrollIndicator={false} contentContainerStyle={{ padding: 24 }}>
        <ContentWidth>
          {!isEdit && (
            <>
              <Label>Team</Label>
              <PillRow>
                {(club?.teams || ["1st XI", "2nd XI", "3rd XI"]).map((t) => (
                  <Pill key={t} active={form.team === t} onPress={() => {
                    setForm({ ...form, team: t, opponent: "" }); setOpponentSearch("");
                    setManualEntry(false); setShowImport(false); setImportResult(null);
                  }}>
                    <PillText active={form.team === t}>{t}</PillText>
                  </Pill>
                ))}
              </PillRow>
            </>
          )}

          <Label>Opponent</Label>
          {showImport ? (
            <ImportBox>
              <ImportDesc>Paste the link to your {form.team}'s division page on Play-Cricket.</ImportDesc>
              <Input style={{ marginTop: 8 }} placeholder="https://ncl.play-cricket.com/website/division/..." placeholderTextColor={colors.inkMuted} value={importUrl} onChangeText={setImportUrl} autoCapitalize="none" autoCorrect={false} />
              {importError ? <ImportError>{importError}</ImportError> : null}
              {!importResult ? (
                <ImportActions>
                  <ImportBtn onPress={handleImport} disabled={importing || !importUrl.trim()}>
                    {importing ? <ActivityIndicator color="#fff" size="small" /> : <ImportBtnText>Fetch teams</ImportBtnText>}
                  </ImportBtn>
                  <Pressable onPress={() => setShowImport(false)}><CancelBtnText>Cancel</CancelBtnText></Pressable>
                </ImportActions>
              ) : (
                <>
                  <ImportFoundText>Found {importResult.teams.length} teams in {importResult.divisionName || "this division"}</ImportFoundText>
                  <ChipWrap>
                    {importResult.teams.slice(0, 6).map((t) => <Chip key={t}><ChipText>{t}</ChipText></Chip>)}
                    {importResult.teams.length > 6 && <Chip><ChipText>+{importResult.teams.length - 6} more</ChipText></Chip>}
                  </ChipWrap>
                  <ImportBtn style={{ marginTop: 8 }} onPress={handleSaveImport} disabled={savingImport}>
                    {savingImport ? <ActivityIndicator color="#fff" size="small" /> : <ImportBtnText>Save for {form.team}</ImportBtnText>}
                  </ImportBtn>
                </>
              )}
            </ImportBox>
          ) : showDropdownList ? (
            <>
              <Input placeholder="Search opponents..." placeholderTextColor={colors.inkMuted} value={form.opponent || opponentSearch} onChangeText={(v: string) => { setOpponentSearch(v); setForm({ ...form, opponent: "" }); }} />
              {opponentSearch.length > 0 && !form.opponent && (
                <Dropdown>
                  {filteredOpponents.map((opp) => (
                    <DropdownItem key={opp} onPress={() => { setForm({ ...form, opponent: opp }); setOpponentSearch(""); }}>
                      <DropdownText>{opp}</DropdownText>
                    </DropdownItem>
                  ))}
                  <DropdownItem onPress={() => { setForm({ ...form, opponent: opponentSearch }); setOpponentSearch(""); }}>
                    <DropdownTextCustom>Use "{opponentSearch}"</DropdownTextCustom>
                  </DropdownItem>
                </Dropdown>
              )}
              {form.opponent ? (
                <SelectedOpponent onPress={() => { setForm({ ...form, opponent: "" }); setOpponentSearch(""); }}>
                  <SelectedOpponentText>{form.opponent}</SelectedOpponentText>
                  <SelectedOpponentText>✕</SelectedOpponentText>
                </SelectedOpponent>
              ) : null}
              <Pressable onPress={() => setManualEntry(true)}><LinkText>Enter manually instead</LinkText></Pressable>
            </>
          ) : (
            <>
              <Input placeholder="e.g. Oundle CC" placeholderTextColor={colors.inkMuted} value={form.opponent} onChangeText={(v: string) => setForm({ ...form, opponent: v })} />
              {!hasLeagueForTeam && canCreate && (
                <Pressable onPress={() => { setShowImport(true); setManualEntry(false); }}><LinkText>Import opponents from Play-Cricket →</LinkText></Pressable>
              )}
              {hasLeagueForTeam && (
                <Pressable onPress={() => setManualEntry(false)}><LinkText>← Back to opponent list</LinkText></Pressable>
              )}
            </>
          )}

          <Label>Date</Label>
          <DateInput value={form.date} onChange={(v) => setForm({ ...form, date: v })} />

          <Label>Time</Label>
          <TimeInput value={form.time} onChange={(v) => setForm({ ...form, time: v })} />

          <Label>Venue</Label>
          <PillRow>
            {(["home", "away"] as MatchVenue[]).map((v) => (
              <Pill key={v} active={form.venue === v} onPress={() => setForm({ ...form, venue: v })}>
                <PillText active={form.venue === v}>{v.charAt(0).toUpperCase() + v.slice(1)}</PillText>
              </Pill>
            ))}
          </PillRow>

          <Label>Location</Label>
          <Input placeholder="Postcode or address (e.g. PE3 8DU)" placeholderTextColor={colors.inkMuted} value={form.location} onChangeText={(v: string) => setForm({ ...form, location: v })} />

          <Label>Type</Label>
          <PillRow>
            {(["league", "cup", "friendly"] as MatchType[]).map((t) => (
              <Pill key={t} active={form.type === t} onPress={() => setForm({ ...form, type: t })}>
                <PillText active={form.type === t}>{t.charAt(0).toUpperCase() + t.slice(1)}</PillText>
              </Pill>
            ))}
          </PillRow>

          {error ? <Text style={{ color: colors.danger, fontSize: 13, fontWeight: "600", marginBottom: 8 }}>{error}</Text> : null}

          <PrimaryBtn onPress={handleCreate} disabled={saving}>
            {saving ? <ActivityIndicator color="#fff" /> : <PrimaryBtnText>{isEdit ? "Save changes" : "Create fixture"}</PrimaryBtnText>}
          </PrimaryBtn>

          <CancelBtn onPress={() => router.back()}><CancelBtnText>Cancel</CancelBtnText></CancelBtn>
          <Spacer h={40} />
        </ContentWidth>
      </KeyboardAwareScroll>
    </Screen>
  );
}
